/**
 * Which results share a card. One definition for the results renderer
 * (components/SgpResultsRenderer.jsx) and the result counts (utils/resultCounts),
 * so "N lines in M cards" counts the cards on screen. Extracted from the
 * renderer Oct 1, 2026.
 *
 * A card is one game + subject (team, player, or "game total") + market. Both
 * sides (Over/Under, −/+) share a card since Oct 1, 2026 (utils/cardSides);
 * before, each side was its own card.
 */

export const isPlayerPropQuery = (option) => {
    const market = (option?.raw?.market || option?.title || '').toLowerCase();
    return market.includes('player') || 
           market.includes('points') ||
           market.includes('rebounds') ||
           market.includes('assists') ||
           market.includes('threes') ||
           market.includes('passing') ||
           market.includes('rushing') ||
           market.includes('receiving') ||
           market.includes('touchdown') ||
           market.includes('goals') ||
           market.includes('saves') ||
           market.includes('shots');
};

// ⭐ COMPREHENSIVE: Check if this is a market with alt lines that should be grouped
// Includes: Spreads, Totals, Team Props, Handicaps, etc.
export const isGroupableMarket = (option) => {
    const market = (option?.raw?.market || option?.title || '').toLowerCase();
    const selection = (option?.raw?.selection || option?.selection || '').toLowerCase();
    const lineValue = option?.lineValue || option?.raw?.selection_line || option?.raw?.handicap || option?.raw?.line;

    // Must have a line value to be groupable
    if (!lineValue && lineValue !== 0) return false;

    // Spread markets (Point Spread, Handicap, Run Line, Puck Line)
    if (market.includes('spread') || market.includes('handicap') || 
        market.includes('run line') || market.includes('puck line') ||
        market.includes('asian handicap')) {
        return true;
    }

    // Totals markets (Game Totals, Team Totals)
    if (market.includes('total') && /\b(over|under)\b/i.test(selection)) {
        return true;
    }

    // Team props with lines
    if ((market.includes('team') || market.includes('1st half') || market.includes('2nd half') || 
         market.includes('1st quarter') || market.includes('1st period')) &&
        (/\b(over|under)\b/i.test(selection) || 
         /[+-]?\d+\.?\d*/.test(selection))) {
        return true;
    }

    // Alt lines explicitly marked
    if (market.includes('alt ') || market.includes('alternate')) {
        return true;
    }

    return false;
};

// Extract direction from selection (over/under or +/-)
export const getDirection = (selection, lineValue) => {
    const sel = (selection || '').toLowerCase();
    if (/\bover\b/i.test(sel)) return 'over';
    if (/\bunder\b/i.test(sel)) return 'under';
    // For spreads, direction is based on line sign
    const line = parseFloat(lineValue);
    if (!isNaN(line)) {
        return line >= 0 ? 'plus' : 'minus';
    }
    return '';
};

// Extract team/subject name from selection
// ⭐⭐⭐ IMPORTANT: Normalize team names to nicknames for consistent grouping
// "Atlanta Hawks" and "Hawks" should both become "hawks"
export const extractSubject = (selection, lineValue, rawData) => {
    // "Donovan Mitchell Over 24.5" / "Cavaliers Under 110.5" / "Over 209.5": drop the
    // side word wherever it sits around the line, so both sides share a subject.
    let subject = selection
        .replace(/\s*\b(over|under)\s*[+-]?\d+\.?\d*\s*$/i, '')
        .replace(/\s*[+-]?\d+\.?\d*\s*$/g, '')
        .replace(/\s*\b(over|under)\s*$/i, '')
        .replace(/^\s*(over|under)\b\s*/i, '')
        .trim();
    // "Over 209.5" / "Under 209.5": the row's player when it names one, else the
    // game total — one subject for both sides. (A bare "Under" used to match
    // "Thunder" by substring, Oct 1, 2026.)
    if (!subject) return String(rawData?.player_name || '').trim() || 'game total';

    const homeTeam = (rawData?.home_team || '').toLowerCase();
    const awayTeam = (rawData?.away_team || '').toLowerCase();
    const subjectLower = subject.toLowerCase();
    // Whole words only: "under" is inside "thunder", "ram" inside "rams".
    const wordIn = (hay, needle) => needle.length > 0 &&
        new RegExp(`(^|\\s)${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`).test(hay);

    if (homeTeam && (wordIn(subjectLower, homeTeam) || wordIn(homeTeam, subjectLower))) {
        return homeTeam;
    }
    if (awayTeam && (wordIn(subjectLower, awayTeam) || wordIn(awayTeam, subjectLower))) {
        return awayTeam;
    }

    const MULTI_WORD_NICKNAMES = [
        'trail blazers', 'blue jackets', 'maple leafs', 'red sox', 'white sox',
        'red wings', 'blue jays', 'golden knights', 'wild card', 'thunder',
        '76ers', 'sixers', '49ers'
    ];

    let foundMultiWord = false;
    for (const multiWord of MULTI_WORD_NICKNAMES) {
        if (subjectLower.endsWith(multiWord) || subjectLower === multiWord) {
            subject = multiWord;
            foundMultiWord = true;
            break;
        }
    }

    if (!foundMultiWord && subject.includes(' ')) {
        const words = subject.split(/\s+/);
        const lastWord = words[words.length - 1];

        const looksLikeTeam = /^[A-Z]/.test(lastWord) && 
            (lastWord.endsWith('s') || lastWord.endsWith('ers') || lastWord.endsWith('ors') || 
             lastWord.endsWith('ics') || lastWord.endsWith('awks') || lastWord.endsWith('eat') ||
             lastWord.endsWith('avs') || lastWord.endsWith('ets') || lastWord.endsWith('ans'));

        if (looksLikeTeam) {
            subject = lastWord;
        }
    }

    return subject;
};

const lineValueOf = (option) => {
    const raw = option?.raw || option;
    return option?.lineValue || raw?.selection_line || raw?.handicap || raw?.line || '';
};

/** The card an option belongs to, or null when it renders as its own card. */
export function cardGroupKey(option) {
    if (!option || option._isCachedSGP) return null;
    const raw = option?.raw || option;
    const selection = raw?.selection || option?.selection || '';
    const market = raw?.market || option?.title || '';
    const lineValue = lineValueOf(option);
    const fixtureId = raw?.fixture_id || raw?.game_id || '';
    const backendAltLines = raw?.alternateLines || option?.alternateLines;
    if (backendAltLines && backendAltLines.length > 0) {
        const subject = extractSubject(selection, lineValue, raw);
        return `${fixtureId}|${subject?.toLowerCase() || 'unknown'}|${market.toLowerCase().replace(/player\s*/i, '')}`;
    }
    if (isGroupableMarket(option) || isPlayerPropQuery(option)) {
        const subject = extractSubject(selection, lineValue, raw);
        if (!subject) return null;
        const normalizedMarket = market.toLowerCase().replace(/player\s*/i, '').replace(/1st\s+half/i, 'h1').replace(/2nd\s+half/i, 'h2').replace(/\s+/g, '_');
        return `${fixtureId}|${subject.toLowerCase()}|${normalizedMarket}`;
    }
    return null;
}

/** Cards the renderer shows for these (already filtered) options. */
export function countCards(options) {
    const keys = new Set();
    let singles = 0;
    for (const option of options || []) {
        const key = cardGroupKey(option);
        if (key === null) singles++;
        else keys.add(key);
    }
    return keys.size + singles;
}
